import React, { useCallback } from 'react';
import { motion } from 'framer-motion';
import { Button, message, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSprayCalculator } from './hooks/useSprayCalculator';
import { useCalculateSpray } from './hooks/useSprayCalculatorData';
import StepWizard from './components/StepWizard';
import ResetButton from './components/ResetButton';
import Step1Sprayer from './components/steps/Step1Sprayer';
import Step2Area from './components/steps/Step2Area';
import Step3Treatment from './components/steps/Step3Treatment';
import ResultsScreen from './components/results/ResultsScreen';
import { STEPS } from './constants';
import { BackgroundSection } from '@client/feature/activeSubstance/partials';
import TutorialButton from '@client/components/ui/TutorialButton';
import CalculatorBackground from '../../../img/calculator_bg.svg';
import CalculateIcon from '../../../img/client/icons/calculate_action.svg';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { usePageMeta } from '@client/hooks/usePageMeta';

/**
 * SprayCalculator Main Component
 * Multi-step calculator for spray/pulverization calculations
 * Follows the same architecture as RiskCalculator
 */
const SprayCalculator = () => {
  const { t } = useTranslation('sprayCalculator');
  usePageMeta({
    title: t('seo_page_title'),
    description: t('seo_page_description'),
    breadcrumbTitle: t('seo_breadcrumb_title'),
    breadcrumbDescription: t('seo_breadcrumb_description'),
  });

  const {
    currentStep,
    currentView,
    selections,
    enrichedData,
    results,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    goToResults,
    goToForm,
    setTankVolume,
    setTankVolumeUnitId,
    setTotalArea,
    setAreaUnitId,
    setApplicationType,
    setBandPercentage,
    setRowSpacing,
    setRowSpacingUnitId,
    setBandWidth,
    setBandWidthUnitId,
    setWaterVolume,
    setWaterVolumeUnitId,
    setProducts,
    setResults,
    reset,
    isStepValid,
    canProceedToNextStep,
    canCalculate,
    isFirstStep,
    isLastStep,
  } = useSprayCalculator();

  const isTransitioningToResults = React.useRef(false);

  const handleStepClick = useCallback(
    (step) => goToStep(step),
    [goToStep]
  );

  const handleReset = useCallback(() => {
    reset();
  }, [reset]);

  const hasAutoCalculatedRef = React.useRef(false);

  // Calculate spray mutation
  const { mutate: calculateSpray, isPending: isCalculating } =
    useCalculateSpray({
      onSuccess: (data) => {
        isTransitioningToResults.current = true;
        setResults(data);
        goToResults();
        message.success(t('calculation_complete'));
        setTimeout(() => {
          isTransitioningToResults.current = false;
        }, 100);
      },
      onError: (error) => {
        console.error('Spray calculation error:', error);
        message.error(
          error.response?.data?.detail ||
            error.response?.data?.errors?.[0] ||
            error.message ||
            t('calculation_error')
        );
      },
    });

  const handleCalculate = useCallback(() => {
    if (!canCalculate) return;

    const payload = {
      tank_volume: parseFloat(selections.tankVolume),
      tank_volume_unit_id: selections.tankVolumeUnitId,
      total_area: parseFloat(selections.totalArea),
      area_unit_id: selections.areaUnitId,
      application_type: selections.applicationType,
      water_volume_per_area: parseFloat(selections.waterVolume),
      water_volume_per_area_unit_id: selections.waterVolumeUnitId,
      products: (selections.products || [])
        .filter((p) => p.id && p.dose && p.unitId)
        .map((p) => ({
          product_id: p.id,
          dose_value: parseFloat(p.dose),
          dose_unit_id: p.unitId,
        })),
    };

    // Add band parameters if applicable
    if (selections.applicationType === 'band') {
      if (selections.bandPercentage) {
        payload.band_percentage = parseFloat(selections.bandPercentage);
      }
      if (selections.rowSpacing && selections.rowSpacingUnitId) {
        payload.row_spacing = parseFloat(selections.rowSpacing);
        payload.row_spacing_unit_id = selections.rowSpacingUnitId;
      }
      if (selections.bandWidth && selections.bandWidthUnitId) {
        payload.band_width = parseFloat(selections.bandWidth);
        payload.band_width_unit_id = selections.bandWidthUnitId;
      }
    }

    calculateSpray(payload);
  }, [canCalculate, selections, calculateSpray]);

  // When the page is opened on view=results without calculated data (e.g. via a
  // shared link), auto-run the calculation once the URL selections are complete.
  // Don't redirect to form while we're waiting — nuqs can settle across a few
  // renders, and shared links rely on staying on view=results.
  React.useEffect(() => {
    if (isTransitioningToResults.current) return;
    if (currentView !== 'results' || results) return;
    if (!canCalculate || hasAutoCalculatedRef.current) return;

    hasAutoCalculatedRef.current = true;
    handleCalculate();
  }, [currentView, results, canCalculate, handleCalculate]);

  const renderStepContent = () => {
    switch (currentStep) {
      case STEPS.SPRAYER:
        return (
          <div className="step-content">
            <Step1Sprayer
              selections={selections}
              setTankVolume={setTankVolume}
              setTankVolumeUnitId={setTankVolumeUnitId}
              enrichedData={enrichedData}
            />
          </div>
        );

      case STEPS.AREA:
        return (
          <div className="step-content">
            <Step2Area
              selections={selections}
              setTotalArea={setTotalArea}
              setAreaUnitId={setAreaUnitId}
              setApplicationType={setApplicationType}
              setBandPercentage={setBandPercentage}
              setRowSpacing={setRowSpacing}
              setRowSpacingUnitId={setRowSpacingUnitId}
              setBandWidth={setBandWidth}
              setBandWidthUnitId={setBandWidthUnitId}
              enrichedData={enrichedData}
            />
          </div>
        );

      case STEPS.TREATMENT:
        return (
          <div className="step-content">
            <Step3Treatment
              selections={selections}
              setWaterVolume={setWaterVolume}
              setWaterVolumeUnitId={setWaterVolumeUnitId}
              setProducts={setProducts}
              enrichedData={enrichedData}
            />
          </div>
        );

      default:
        return (
          <div className="step-content">
            <Step1Sprayer
              selections={selections}
              setTankVolume={setTankVolume}
              setTankVolumeUnitId={setTankVolumeUnitId}
              enrichedData={enrichedData}
            />
          </div>
        );
    }
  };

  // Results view
  if (currentView === 'results' && results) {
    return (
      <div className="relative w-full">
        <div className="container px-4 sm:px-6 lg:px-8">
          <BackgroundSection
            backgroundSource={CalculatorBackground}
            showBottomBg={false}
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex flex-col gap-6 sm:gap-8 relative z-10">
              <div className="filters-container">
                <ResultsScreen
                  results={results}
                  selections={selections}
                  enrichedData={enrichedData}
                  onReset={handleReset}
                  onGoToForm={goToForm}
                  isStepValid={isStepValid}
                  onStepClick={handleStepClick}
                  currentStep={currentStep}
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Form view
  return (
    <div className="relative w-full">
      <div className="container px-4 sm:px-6 lg:px-8">
        <BackgroundSection
          backgroundSource={CalculatorBackground}
          showBottomBg={false}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col gap-6 sm:gap-8 relative z-10">
            <div className="filters-container">
              <Spin
                prefixCls="step-wizard"
                spinning={isCalculating}
                tip={t('calculating')}
              >
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0 mb-4">
                  <h2 className="text-xl sm:text-2xl lg:text-3xl normal-case font-medium m-0">
                    {t('title')}
                  </h2>
                  <div className="flex flex-wrap items-center gap-2">
                    <TutorialButton slotKey="spray-calculator-criteria" />
                    <ResetButton onReset={handleReset} />
                  </div>
                </div>

                {/* Step Wizard */}
                <StepWizard
                  currentStep={currentStep}
                  onStepClick={handleStepClick}
                  isStepValid={isStepValid}
                  selections={selections}
                  enrichedData={enrichedData}
                />

                {/* Step Content */}
                {renderStepContent()}

                {/* Navigation Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 justify-center items-stretch sm:items-center mt-6">
                  {!isFirstStep && (
                    <Button
                      type="default"
                      onClick={goToPreviousStep}
                      className="w-full sm:w-auto h-11 sm:h-[50px] px-4 sm:px-6 text-sm sm:text-base"
                      icon={<ArrowLeftOutlined />}
                    >
                      {t('button_previous')}
                    </Button>
                  )}

                  {!isLastStep && (
                    <Button
                      type="primary"
                      onClick={goToNextStep}
                      disabled={!canProceedToNextStep}
                      className="w-full sm:w-auto h-11 sm:h-[50px] px-4 sm:px-6 text-sm sm:text-base"
                      icon={<ArrowRightOutlined />}
                      iconPosition="end"
                      style={{
                        cursor: canProceedToNextStep ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {t('button_next')}
                    </Button>
                  )}

                  {isLastStep && (
                    <Button
                      type="primary"
                      className="w-full sm:w-auto h-11 sm:h-[50px] px-4 sm:px-6 text-sm sm:text-base"
                      onClick={handleCalculate}
                      disabled={!canCalculate || isCalculating}
                      icon={
                        <img src={CalculateIcon} alt="" className="w-5 h-5" />
                      }
                      style={{
                        cursor:
                          canCalculate && !isCalculating
                            ? 'pointer'
                            : 'not-allowed',
                      }}
                    >
                      {isCalculating ? t('calculating') : t('button_calculate')}
                    </Button>
                  )}
                </div>
              </Spin>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SprayCalculator;
